import React, { useState, useEffect } from 'react';
import { Position } from '../types';
import { FaTimes } from 'react-icons/fa';

interface GameStatusProps {
  statsPosition: Position;
  isDraggingStats: React.MutableRefObject<boolean>;
  handleDragStart: (
    e: React.MouseEvent,
    isDragging: React.MutableRefObject<boolean>,
    currentPosition: Position
  ) => void;
  handleDragEnd: (isDragging: React.MutableRefObject<boolean>) => void;
  totalScore: number;
  gameType: string;
  difficulty: string;
  status?: string;
  isSpeaking: boolean;
  bgMusicVolume: number;
  gameSoundVolume: number;
  setBgMusicVolume: (volume: number) => void;
  setGameSoundVolume: (volume: number) => void;
  onExitClick: () => void;
  gameId?: string;
  reactionType?: string;
  onClose: () => void;
}

const GameStatus: React.FC<GameStatusProps> = ({
  statsPosition,
  isDraggingStats,
  handleDragStart,
  handleDragEnd,
  totalScore,
  gameType,
  difficulty,
  status,
  isSpeaking,
  bgMusicVolume,
  gameSoundVolume,
  setBgMusicVolume,
  setGameSoundVolume,
  onExitClick,
  gameId,
  reactionType,
  onClose
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentReaction, setCurrentReaction] = useState<string | null>(null);

  useEffect(() => {
    if (reactionType) {
      setCurrentReaction(reactionType);
      setCurrentSlide(2);
    } else if (currentReaction && !reactionType) {
      setCurrentReaction(null);
      setCurrentSlide(0);
    }
  }, [reactionType, currentReaction]);

  const getStatusType = (status: string): string => {
    const statusMap: { [key: string]: string[] } = {
      danger: ['critical', 'perilous', 'threatened', 'unstable'],
      success: ['advancing', 'progressing', 'triumphant', 'secure'],
      neutral: ['stable', 'unchanged', 'maintaining', 'steady'],
      opportunity: ['advantageous', 'promising', 'favorable', 'fortunate'],
      setback: ['challenged', 'hindered', 'complicated', 'troubled']
    };

    for (const [type, statuses] of Object.entries(statusMap)) {
      if (statuses.includes(status.toLowerCase())) {
        return type;
      }
    }
    return 'neutral';
  };

  const calculateTrustLevel = (score: number): { level: string; color: string } => {
    if (score <= -10) return { level: 'Severed', color: 'var(--danger-color)' };
    if (score <= -5) return { level: 'Critical', color: 'var(--danger-color)' };
    if (score <= 0) return { level: 'Weakening', color: 'var(--warning-color)' };
    if (score <= 5) return { level: 'Stable', color: 'var(--text-color)' };
    if (score <= 10) return { level: 'Strong', color: 'var(--success-color)' };
    return { level: 'Unbreakable', color: 'var(--success-color)' };
  };

  const getVideoSource = (reaction: string) => {
    if (!gameType) {
      console.error('No gameType provided to getVideoSource');
      return '';
    }

    const reactionCategories: { [key: string]: string } = {
      // Positive reactions
      'trusting': 'positive',
      'thankful': 'positive',
      'relieved': 'positive',
      'confident': 'positive',
      'hopeful': 'positive',
      'grateful': 'positive',
      
      // Negative reactions
      'skeptical': 'negative',
      'resistant': 'negative',
      'defiant': 'negative',
      'distrustful': 'negative',
      'concerned': 'negative',
      'worried': 'negative',
      'fearful': 'negative',
      'doubtful': 'negative',
      
      // Neutral reactions
      'considering': 'neutral',
      'pondering': 'neutral',
      'uncertain': 'neutral',
      'hesitant': 'neutral',
      'contemplative': 'neutral',
      'thoughtful': 'neutral',
      
      // Surprise reactions
      'stunned': 'surprise',
      'enlightened': 'surprise',
      'overwhelmed': 'surprise',
      'awakened': 'surprise',
      'shocked': 'surprise',
      'amazed': 'surprise',
      
      // Urgent reactions
      'anxious': 'urgent',
      'pressured': 'urgent',
      'stressed': 'urgent',
      'desperate': 'urgent',
      'threatened': 'urgent',
      'panicked': 'urgent'
    };
    
    let category = reaction.toLowerCase();
    
    if (reactionCategories[category]) {
      category = reactionCategories[category];
    }
    
    if (!['positive', 'negative', 'neutral', 'surprise', 'urgent'].includes(category)) {
      category = 'neutral';
    }
    
    let gameFolder = gameType.toLowerCase().replace(/ /g, '_');
    
    const folderMapping: { [key: string]: string } = {
      'fairy_realm': 'fairy_realm',
      'space_explorer': 'space_explorer',
      'detective': 'detective',
      'apocalypse': 'apocalypse',
      'hitler_assassination': 'hitler_assassination',
      // Add common variations
      'fairy realm': 'fairy_realm',
      'space explorer': 'space_explorer',
      'hitler assassination': 'hitler_assassination'
    };
    
    if (folderMapping[gameFolder]) {
      gameFolder = folderMapping[gameFolder];
    } else {
      const availableFolders = ['fairy_realm', 'space_explorer', 'detective', 'apocalypse', 'hitler_assassination'];
      const matchingFolder = availableFolders.find(folder => 
        gameFolder.includes(folder) || folder.includes(gameFolder)
      );
      
      if (matchingFolder) {
        gameFolder = matchingFolder;
      }
    }
    
    const videoPath = `/reactions/${gameFolder}/${category}.mp4`;
    return videoPath;
  };

  const slides = [
    {
      title: 'GAME STATS',
      icon: 'lni-stats-up',
      content: (
        <div className="stats-slide">
          <div className="stat-row">
            <div className="tooltip-wrapper">
              <div className="stat-icon">
                <i className="lni lni-star"></i>
              </div>
              <span className={`stat-value ${
                totalScore < 0 ? 'negative' : 
                totalScore < 5 ? 'low' : 
                totalScore > 20 ? 'high' : 
                'normal'
              }`}>
                {totalScore}
              </span>
              <div className="tooltip">Current score based on your guidance decisions</div>
            </div>
          </div>

          <div className="stat-row">
            <div className="tooltip-wrapper">
              <div className="stat-icon">
                <i className="lni lni-heart"></i>
              </div>
              <span 
                className="stat-value"
                style={{ color: calculateTrustLevel(totalScore).color }}
              >
                {calculateTrustLevel(totalScore).level}
              </span>
              <div className="tooltip">Strength of your spiritual connection with the descendant</div>
            </div>
          </div>

          <div className="stat-row">
            <div className="tooltip-wrapper">
              <div className="stat-icon">
                <i className="lni lni-game"></i>
              </div>
              <span className="stat-value">
                {gameType
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, char => char.toUpperCase())
                }
              </span>
              <div className="tooltip">Current scenario type you're guiding through</div>
            </div>
          </div>

          <div className="stat-row">
            <div className="tooltip-wrapper">
              <div className="stat-icon">
                <i className="lni lni-target"></i>
              </div>
              <span className="stat-value">
                {difficulty.toLowerCase()}
              </span>
              <div className="tooltip">Challenge level of the current scenario</div>
            </div>
          </div>

          {totalScore <= -5 && (
            <div className="trust-warning">
              <div className="tooltip-wrapper">
                <div className="warning-icon">
                  <i className="lni lni-warning"></i>
                </div>
                <span>The spiritual bond weakens dangerously</span>
                <div className="tooltip">Warning: Your connection is at risk of being severed</div>
              </div>
            </div>
          )}

          {status && (
            <div className={`game-status status-${getStatusType(status)}`}>
              <div className="tooltip-wrapper">
                <i className="lni lni-warning"></i>
                <span>{status.toLowerCase()}</span>
                <div className="tooltip">Current state of the scenario</div>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'SETTINGS',
      icon: 'lni-cog',
      content: (
        <div className="settings-slide">
          <div className="volume-controls">
            <div className="volume-control">
              <div className="tooltip-wrapper">
                <div className="stat-icon">
                  <i className="lni lni-music"></i>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={bgMusicVolume}
                  onChange={(e) => setBgMusicVolume(parseInt(e.target.value))}
                  className="volume-slider"
                  style={{ '--value': `${bgMusicVolume}%` } as React.CSSProperties}
                />
                <div className="tooltip">Background Music Volume</div>
              </div>
            </div>
            <div className="volume-control">
              <div className="tooltip-wrapper">
                <div className="stat-icon">
                  <i className="lni lni-volume-high"></i>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={gameSoundVolume}
                  onChange={(e) => setGameSoundVolume(parseInt(e.target.value))}
                  className="volume-slider"
                  style={{ '--value': `${gameSoundVolume}%` } as React.CSSProperties}
                />
                <div className="tooltip">Game Sound Volume</div>
              </div>
            </div>
          </div>
          <button className="exit-button" onClick={onExitClick}>
            <i className="lni lni-exit"></i>
            Exit Game
          </button>
        </div>
      )
    },
    {
      title: 'REACTION',
      icon: 'lni-video',
      content: (
        <div className="reaction-slide">
          <div className="reaction-video-container">
            {gameId && currentReaction && gameType ? (
              <>
                <div className="reaction-info">
                  {currentReaction && (
                    <span className="reaction-type">
                      {currentReaction.charAt(0).toUpperCase() + currentReaction.slice(1)}
                    </span>
                  )}
                </div>
                <video
                  className="reaction-video"
                  autoPlay
                  loop
                  muted
                  playsInline
                  onError={(e) => {
                    const target = e.target as HTMLVideoElement;
                    target.style.display = 'none';
                    
                    const container = target.parentElement;
                    if (container) {
                      const errorMsg = document.createElement('div');
                      errorMsg.className = 'no-reaction';
                      errorMsg.innerHTML = `<p>Failed to load reaction video</p><span class="reaction-hint">Path: ${getVideoSource(currentReaction)}</span>`;
                      container.appendChild(errorMsg);
                    }
                  }}
                >
                  <source src={getVideoSource(currentReaction)} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </>
            ) : (
              <div className="no-reaction">
                <p>Waiting for character reaction...</p>
                <span className="reaction-hint">Reactions will appear here after your actions</span>
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      title: 'SPEAKING',
      icon: 'lni-mic',
      content: (
        <div className="speaking-slide">
          <div className="speaking-animation">
            {isSpeaking && (
              <div className="speaking-dots">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
          </div>
        </div>
      )
    }
  ];

  return (
    <div 
      className="floating-component game-stats"
      style={{ 
        transform: `translate(${statsPosition.x}px, ${statsPosition.y}px)`,
        '--nav-speaking-size': '2px',
        '--nav-speaking-scale': '0.35',
      } as React.CSSProperties}
      onMouseDown={(e) => handleDragStart(e, isDraggingStats, statsPosition)}
      onMouseUp={() => handleDragEnd(isDraggingStats)}
    >
      <div className="floating-inner bg">
        <div className="floating-header">
          <h3>{slides[currentSlide].title}</h3>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className="slider-container">
          {slides.map((slide, index) => (
            <div 
              key={index} 
              className={`slide ${currentSlide === index ? 'active' : ''}`}
            >
              {slide.content}
            </div>
          ))}
        </div>

        <div className="slider-nav">
          {slides.map((slide, index) => (
            <button
              key={index}
              className={`nav-button ${currentSlide === index ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
            >
              {index === 3 && isSpeaking ? (
                <div className="speaking-dots nav-speaking" style={{
                  transform: `scale(var(--nav-speaking-scale))`,
                  margin: '-4px auto',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              ) : (
                <i className={`lni ${slide.icon}`}></i>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GameStatus; 